import { Box, Button, Grid, IconButton, styled, Typography } from '@mui/material';
import { NutrientValue, NutritionInfo } from '@pepperhq/menu-sdk';
import { FieldArrayRenderProps, FormikProps } from 'formik';
import React from 'react';
import DragHandle from '@mui/icons-material/DragHandle';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Add from '@mui/icons-material/Add';
import { TextFormField } from 'lib/form/fields/TextFormField';
import DeleteIcon from '@mui/icons-material/Delete';

interface NutritionValuesFormProps extends FieldArrayRenderProps {
    form: FormikProps<NutritionInfo>;
}

interface NutrientValueFormProps {
    remove: (index: number) => void;
    index: number;
}

const PREFIX = 'NutrientValueForm';

const classes = {
    iconContainer: `${PREFIX}-iconContainer`,
    dragIcon: `${PREFIX}-dragIcon`
};

const StyledBox = styled(Box)(({ theme }) => ({
    zIndex: 999999,
    backgroundColor: theme.palette.background.paper,
    [`& .${classes.dragIcon}`]: {
        cursor: 'pointer',
        touchAction: 'none'
    },
    [`& .${classes.iconContainer}`]: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    }
}));

const NutrientValueForm: React.FC<NutrientValueFormProps> = ({ remove, index }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: index });
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform ? { ...transform, x: 0 } : null),
        transition
    };

    const removeItem = React.useCallback(() => {
        remove(index);
    }, [index, remove]);

    return (
        <StyledBox ref={setNodeRef} style={style} zIndex={999999}>
            <Grid container spacing={2}>
                <Grid className={classes.iconContainer} size={1}>
                    <DragHandle className={classes.dragIcon} {...attributes} {...listeners} />
                </Grid>
                <Grid size={5}>
                    <TextFormField name={`values[${index}].nutrient`} />
                </Grid>
                <Grid size={5}>
                    <TextFormField name={`values[${index}].value`} />
                </Grid>
                <Grid className={classes.iconContainer} size={1}>
                    <IconButton onClick={removeItem} size="large">
                        <DeleteIcon color="error" />
                    </IconButton>
                </Grid>
            </Grid>
        </StyledBox>
    );
};

export const NutritionValuesForm: React.FC<NutritionValuesFormProps> = ({ form, remove, move, push }) => {
    const items = React.useMemo(
        () => (form.values?.values || []).map((_: NutrientValue, index: number) => index),
        [form.values?.values]
    );

    const handleDragEnd = React.useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;
            if (over && active.id !== over.id) {
                move(active.id as number, over.id as number);
            }
        },
        [move]
    );

    const handleAddItem = React.useCallback(() => {
        push({ nutrient: '', value: '' });
    }, [push]);

    if (Array.isArray(form.values?.values)) {
        return (
            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={items} strategy={verticalListSortingStrategy}>
                    <Grid container spacing={2}>
                        <Grid size={1}>
                            <div />
                        </Grid>
                        <Grid size={5}>
                            <Typography align="center">Nutrient</Typography>
                        </Grid>
                        <Grid size={5}>
                            <Typography align="center">Value</Typography>
                        </Grid>
                    </Grid>
                    {form.values.values.map((_: NutrientValue, index: number) => (
                        <NutrientValueForm key={`sortable-${index}`} index={index} remove={remove} />
                    ))}
                    <Button variant="outlined" onClick={handleAddItem}>
                        <Add />
                    </Button>
                </SortableContext>
            </DndContext>
        );
    }
};
